import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { brandBackground, defaultFocusVisibleState } from "./buttonColors";

const sizes = {
    xs: { padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`, fontSize: tokens.typography.fontSize["md-12px"], lineHeight: "16px" },
    sm: { padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2-5"]}`, fontSize: tokens.typography.fontSize["lg-14px"], lineHeight: "20px" },
    md: { padding: `${tokens.globals["spacing-1-5"]} ${tokens.globals["spacing-2-5"]}`, fontSize: tokens.typography.fontSize["lg-14px"], lineHeight: "20px" },
    lg: { padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-2-5"]}`, fontSize: tokens.typography.fontSize["lg-14px"], lineHeight: "20px" },
};

const errorState = {
    outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.states.alert.default}`,
    boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const variantConfig = {
    primary: {
        background: brandBackground("--button-color", tokens.colors.brand["aie-blue"]["500"]),
        color: tokens.colors.neutral.white,
        borderColor: "transparent",
        hover: { background: brandBackground("--button-hover-color", tokens.colors.brand["aie-blue"]["600"]) },
        active: { background: brandBackground("--button-active-color", tokens.colors.brand["aie-blue"]["700"]), transform: "scale(0.96)" },
    },

    secondary: {
        background: brandBackground("--button-color", tokens.colors.brand["aie-blue"]["500"]),
        color: tokens.colors.neutral.white,
        borderColor: "transparent",
        hover: { background: brandBackground("--button-hover-color", tokens.colors.brand["aie-blue"]["600"]) },
        active: { background: brandBackground("--button-active-color", tokens.colors.brand["aie-blue"]["700"]), transform: "scale(0.96)" },
    },

    destructive: {
        background: tokens.colors.states.alert["50"],
        color: tokens.colors.states.alert.default,
        borderColor: "transparent",
        hover: { background: brandBackground("--button-hover-color", tokens.colors.states.alert["100"]) },
        active: { background: brandBackground("--button-active-color", tokens.colors.states.alert["50"]), transform: "scale(0.96)" },
    },

    accent: {
        background: tokens.colors.neutral.white,
        color: tokens.colors.text.primary,
        borderColor: tokens.colors.surfaces["border-divider"],
        hover: { background: tokens.colors.neutral.black, color: `${tokens.colors.neutral.white} !important` },
        active: { background: tokens.colors.neutral.grey["200"], transform: "scale(0.96)" },
    },
};

const getVariantStyles = (variant) => {
    const { background, color, borderColor, outline, outlineOffset, hover, active } = variantConfig[variant] || variantConfig.primary;

    return {
        background,
        color,
        borderColor,
        outline,
        outlineOffset,
        "&:hover:not(:disabled)": hover,
        "&:active:not(:disabled)": active,
        "&:focus-visible:not(:disabled)": defaultFocusVisibleState,
        '&[data-error="true"]': errorState,
    };
};

const StyledButton = styled.button(({ size = "lg", variant = "primary", $colorOverride, $borderRadiusOverride }) => {
    const { padding, fontSize, lineHeight } = sizes[size];
    const { background, color: variantColor, borderColor, ...variantStates } = getVariantStyles(variant);
    const color = $colorOverride || variantColor;
    const borderRadius = $borderRadiusOverride || tokens.globals["border-radius"];

    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: tokens.typography.fontWeights.medium,
        cursor: "pointer",
        transition: "all 0.15s ease-in-out",
        background: "transparent",
        color: "inherit",
        borderStyle: "solid",
        borderWidth: tokens.globals["spacing-px"],

        "&:disabled": {
            opacity: 0.50,
            cursor: "not-allowed",
            transform: "none",
        },

        padding,
        fontSize,
        lineHeight,
        borderRadius,

        gap: size === "xs" || size === "sm" ? tokens.globals["spacing-1"] : tokens.globals["spacing-1-5"],

        background,
        color,
        borderColor,
        ...variantStates,

        "&:focus": {
            outline: "none",
        },

        "&:link, &:visited, &:hover, &:active, &:focus": {
            textDecoration: "none !important",
            color: `${color} !important`,
        },
    };
});

/**
 * A reusable button component with multiple visual variants and sizes, styled from tokens.js.
 * @param {string} [props.variant="primary"] "primary", "secondary", "destructive", or "accent"
 * @param {string} [props.size="lg"] "xs", "sm", "md", or "lg"
 * @param {string} [props.colorOverride] Hardcodes text/icon color outside the token system (see NegativeButton).
 * @param {string} [props.borderRadiusOverride] Hardcodes border-radius, bypassing the default token.
 * Also: disabled, error, children, ref (self-explanatory).
 */
const Button = forwardRef(
    ({ variant = "primary", size = "lg", disabled = false, error = false, colorOverride, borderRadiusOverride, children, ...props }, ref) => {
        return (
            <StyledButton
                ref={ref}
                variant={variant}
                size={size}
                disabled={disabled}
                $colorOverride={colorOverride}
                $borderRadiusOverride={borderRadiusOverride}
                data-error={error ? "true" : undefined}
                {...props}
            >
                {children}
            </StyledButton>
        );
    }
);

Button.displayName = "Button";

export default Button;
